/** @format */

import React, { useState } from "react";
import { IUsers } from "../AccountInfoPage.types";
import { Row, Table, Button, message, Popconfirm } from "antd";
import Column from "antd/lib/table/Column";
import EditableFirstName from "./EditableNames/EditableFirstName";
import EditableLastName from "./EditableNames/EditableLastName";
import { MailOutlined } from "@ant-design/icons";
import EditablePermissions from "./EditablePermissions/EditablePermissions";
import EditableStatus from "./EditableStatus/EditableStatus";
import DeleteUserModal from "./DeleteUserModal/DeleteUserModal";
import VoiceGainAuthSvcApi from "../../../../apis/VoiceGainAuthSvcApi";
import { ColumnProps } from "antd/lib/table";
import { IAccountsListProps } from "../../AccountsOverview/AccountsOverview.types";

interface IUsersTableViewProps {
	users: IUsers[];
	editedUsers: IUsers[];
	setTheEditedUsers: (editedUsersList) => void;
	fetchAndSetUsers: () => void;
	getColumnSearchProps: (data: string) => {
		filterDropdown: ColumnProps<IAccountsListProps>["filterDropdown"];
		filterIcon: ColumnProps<IAccountsListProps>["filterIcon"];
	};
}

const UsersTableView: React.FC<IUsersTableViewProps> = ({
	users,
	editedUsers,
	setTheEditedUsers,
	fetchAndSetUsers,
	getColumnSearchProps,
}) => {
	const [usersWereEdited, setUsersWereEdited] = useState<boolean>(false);
	const [changesWereUndone, setChangesWereUndone] = useState<boolean>(false);

	const setIfUsersWereEdited = (value) => {
		setUsersWereEdited(value);
	};

	const commitChanges = () => {
		editedUsers.map((user) => {
			if (user.isEdited) {
				VoiceGainAuthSvcApi.modUser(
					user.userId,
					user.email,
					user.accountId,
					user.firstName,
					user.lastName,
					user.status,
					user.permissions || "" // Provide empty string fallback for undefined permissions
				).then((response) => {
					if (response.ok) {
						setUsersWereEdited(false);
						fetchAndSetUsers();
						message.success("Succesfully modified users.", 5);
						return response.json();
					} else {
						setUsersWereEdited(false);
						fetchAndSetUsers();
						message.error(
							"Error modifiying users. Only CMP accounts are allowed to modify users.",
							5
						);
					}
				});
			}
			return false;
		});
	};

	const undoChanges = () => {
		setChangesWereUndone(true);
		setIfUsersWereEdited(false);
	};

	const setUndoChanges = (value) => {
		setChangesWereUndone(value);
	};

	const sendResetEmail = async (email: string) => {
		try {
			let sendResetEmailResponse = await VoiceGainAuthSvcApi.sendResetEmail(
				email
			);
			await sendResetEmailResponse.json();
			if (sendResetEmailResponse.ok) {
				return message.success("Email sent");
			} else throw new Error("Error sending email");
		} catch (err: any) {
			message.error(err.message);
			console.log(err.message);
		}
	};

	return (
		<div>
				<Table 
					dataSource={users} 
					rowKey={"email"}
					scroll={{ x: 'max-content' }}
					style={{ width: '100%' }}
				>
					<Column
						title="Email"
						dataIndex="email"
						key="email"
						width="20%"
						sorter={(a: { email: string }, b: { email: string }) =>
							("" + a.email).localeCompare(b.email)
						}
						{...getColumnSearchProps("email")}
					/>
					<Column<IUsers>
						title="First Name"
						dataIndex="firstName"
						key="firstName"
						width="15%"
						sorter={(a: { firstName: string }, b: { firstName: string }) =>
							("" + a.firstName).localeCompare(b.firstName)
						}
						render={(
							firstName: string,
							record: IUsers,
							index: number
						): React.ReactNode => {
							return (
								<EditableFirstName
									firstName={firstName}
									setTheEditedUsers={setTheEditedUsers}
									editedUsers={editedUsers}
									userId={record.userId}
									setIfUsersWereEdited={setIfUsersWereEdited}
									changesWereUndone={changesWereUndone}
									setUndoChanges={setUndoChanges}
								/>
							);
						}}
					/>

				<Column<IUsers>
					title="Last Name"
					dataIndex="lastName"
					key="lastName"
					width="15%"
					sorter={(a: { lastName: string }, b: { lastName: string }) =>
						("" + a.lastName).localeCompare(b.lastName)
					}
					render={(lastName, record: IUsers) => {
						return (
							<EditableLastName
								lastName={lastName}
								setTheEditedUsers={setTheEditedUsers}
								editedUsers={editedUsers}
								userId={record.userId}
								setIfUsersWereEdited={setIfUsersWereEdited}
								changesWereUndone={changesWereUndone}
								setUndoChanges={setUndoChanges}
							/>
						);
					}}
				/>
				<Column
					title="Permissions"
					dataIndex="permissions"
					key="permissions"
					width="20%"
					onFilter={(
						permissions: string | number | boolean,
						record: IUsers
					) => {
						if (record.permissions) {
							return (
								record.permissions.indexOf(permissions.toString()) !== -1
							);
						}
						return false;
					}}
					filters={[
						{ text: "admin", value: "admin" },
						{ text: "billing", value: "billing" },
						{ text: "cmp", value: "cmp" },
						{ text: "greg", value: "greg" },
					]}
					render={(permissions, record: IUsers) => {
						return (
							<EditablePermissions
								permissions={permissions}
								setTheEditedUsers={setTheEditedUsers}
								editedUsers={editedUsers}
								userId={record.userId}
								setIfUsersWereEdited={setIfUsersWereEdited}
								changesWereUndone={changesWereUndone}
								setUndoChanges={setUndoChanges}
							/>
						);
					}}
				/>
				<Column<IUsers>
					title="Status"
					dataIndex="status"
					key="status"
					align="center"
					width="10%"
					sorter={(a: { status: string }, b: { status: string }) =>
						("" + a.status).localeCompare(b.status)
					}
					onFilter={(status: string | number | boolean, record: IUsers) => {
						if (record.status) {
							return record.status.indexOf(status.toString()) === 0;
						}
						return false;
					}}
					filters={[
						{ text: "CREATED", value: "CREATED" },
						{ text: "ACTIVE", value: "ACTIVE" },
						{ text: "REVOKED", value: "REVOKED" },
						{ text: "LOCKED", value: "LOCKED" },
						{ text: "DELETED", value: "DELETED" },
					]}
					render={(status, record: IUsers) => {
						return (
							<EditableStatus
								status={status}
								setTheEditedUsers={setTheEditedUsers}
								editedUsers={editedUsers}
								userId={record.userId}
								setIfUsersWereEdited={setIfUsersWereEdited}
								changesWereUndone={changesWereUndone}
								setUndoChanges={setUndoChanges}
							/>
						);
					}}
				/>
				<Column
					title="Send Reset Email"
					key="resetEmail"
					align="center"
					width="10%"
					render={(text, record: IUsers) => {
						return (
							<Popconfirm
								title="Send reset password email to user?"
								onConfirm={() => sendResetEmail(record.email)}
								okText="Yes"
								cancelText="No">
								<Button shape="circle">
									<MailOutlined />
								</Button>
							</Popconfirm>
						);
					}}
				/>
				<Column
					title="Delete"
					key="delete"
					align="center"
					width="10%"
					render={(text, record: any) => {
						return (
							<DeleteUserModal
								email={record.email}
								userId={record.userId}
								fetchAndSetUsers={fetchAndSetUsers}
							/>
						);
					}}
				/>
			</Table>
			<div>
				<Button
					onClick={undoChanges}
					disabled={!usersWereEdited}
					color="danger"
					style={{ marginRight: "2px" }}>
					Undo
				</Button>
				<Button
					onClick={commitChanges}
					disabled={!usersWereEdited}
					type="primary"
					style={{ marginLeft: "2px" }}>
					Commit
				</Button>
			</div>
		</div>
	);
};

export default UsersTableView;
